import type { Asset, AssetKind } from "@pax/api-contract/assets/v1alpha1";
import { Button, cn, focusHaloFlush, Skeleton, TruncatedText } from "@pax/ui";
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SCREEN_SCROLLER } from "@/shell/Screen";
import { pictureOf, titleOf } from "../media";
import { AssetLink, AssetTile, captionDetail, SensitiveLabel, shownTags } from "./AssetTile";
import { PlayButton, PlaybackTime, Waveform } from "./Waveform";

/** The width a column aims for. More room is more columns, not bigger pictures. */
const COLUMN_WIDTH = 240;
const GAP = 12;
/** A picture's caption, two lines that never wrap, which the columns count when they balance. */
export const CAPTION = 48;
/** What a title's second line adds, and roughly how wide a character of it is. */
export const TITLE_LINE = 20;
export const TITLE_CHARACTER = 7.4;
/** The same for the smaller line of tags and author under it. */
const DETAIL_LINE = 16;
const DETAIL_CHARACTER = 6.4;
/** An audio card is the same height whatever its column's width. */
const AUDIO_CARD = 150;

/**
 * What a card's wrapped lines add to its height, estimated from how long each line's text is, so
 * a column of long names is counted as the taller column it is.
 */
export function wraps(text: string, width: number, character: number, line: number): number {
  return text.length * character > width ? line : 0;
}

/** A recording's card: its name and author beside the play button, and its tags at the foot. */
function audioCardLines(asset: Asset, width: number): number {
  const beside = width - 92;
  const author =
    asset.media?.kind === "audio" ? `0:00 · ${asset.owner.displayName}` : asset.owner.displayName;
  return (
    wraps(titleOf(asset), beside, TITLE_CHARACTER, TITLE_LINE) +
    wraps(author, beside, DETAIL_CHARACTER, DETAIL_LINE) +
    wraps(shownTags(asset).join(" · "), width - 64, DETAIL_CHARACTER, DETAIL_LINE)
  );
}

/** How tall an asset's card is in a column this wide, picture and caption. */
function assetHeight(asset: Asset, columnWidth: number): number {
  if (asset.media?.kind === "audio") return AUDIO_CARD + audioCardLines(asset, columnWidth);
  const picture = pictureOf(asset.media);
  return (picture ? (picture.height / picture.width) * columnWidth : columnWidth * 0.75) + CAPTION;
}

/**
 * Cards in columns, each as tall as it needs. Each goes to the column that is
 * shortest so far, which keeps the reading order close to left-to-right while
 * the columns end level.
 *
 * Pages only ever add to the end, and the columns are worked out again from
 * the top each time, so a card already placed stays where it was.
 */
export function Masonry<Item>({
  items,
  keyOf,
  heightOf,
  render,
}: {
  items: Item[];
  keyOf: (item: Item) => string;
  /** How tall the item's card is in a column this wide. Defined once, so the columns aren't rebalanced every render. */
  heightOf: (item: Item, columnWidth: number) => number;
  render: (item: Item, columnWidth: number) => ReactNode;
}) {
  const { measure, width } = useWidth();
  const count = Math.max(2, Math.round((width + GAP) / (COLUMN_WIDTH + GAP)));
  const columnWidth = width > 0 ? (width - GAP * (count - 1)) / count : COLUMN_WIDTH;

  const columns = useMemo(() => {
    const placed: Item[][] = Array.from({ length: count }, () => []);
    const heights = Array<number>(count).fill(0);
    for (const item of items) {
      const shortest = heights.indexOf(Math.min(...heights));
      placed[shortest]?.push(item);
      heights[shortest] = (heights[shortest] ?? 0) + heightOf(item, columnWidth) + GAP;
    }
    return placed;
  }, [items, count, columnWidth, heightOf]);

  return (
    <div ref={measure} className="flex items-start" style={{ gap: GAP }}>
      {columns.map((column, at) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: a column is only its place across the grid
          key={at}
          className="flex min-w-0 flex-1 flex-col"
          style={{ gap: GAP }}
        >
          {column.map(item => (
            <Fragment key={keyOf(item)}>{render(item, columnWidth)}</Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Assets in columns, each at the shape it was made in.
 *
 * Shape is the one thing a grid of flags, portraits and film stills can't
 * take from them: a tall narrow banner cropped to a square is a different
 * picture. So nothing is cropped, and the columns are balanced instead.
 */
export function AssetGrid({
  assets,
  status,
  trailing,
}: {
  assets: Asset[];
  status?: boolean;
  trailing?: (asset: Asset) => ReactNode;
}) {
  return (
    <Masonry
      items={assets}
      keyOf={asset => asset.id}
      heightOf={assetHeight}
      render={(asset, columnWidth) => (
        <AssetTile
          asset={asset}
          sizes={`${Math.ceil(columnWidth)}px`}
          status={status}
          trailing={trailing?.(asset)}
        />
      )}
    />
  );
}

/**
 * Recordings as a list. A wall of sound has nothing to look at, so audio on
 * its own is read like a track list: what it is, how it sounds, how long.
 */
export function AudioList({ assets }: { assets: Asset[] }) {
  return (
    <ul className="flex flex-col gap-1">
      {assets.map(asset =>
        asset.media?.kind === "audio" ? (
          <li
            key={asset.id}
            className="group relative flex items-center gap-4 rounded-xl px-3 py-2.5 transition-colors duration-200 hover:bg-foreground/5"
          >
            <PlayButton media={asset.media} className="z-10" />
            <div className="w-56 min-w-0 shrink-0 max-sm:flex-1">
              <AssetLink
                asset={asset}
                className={`block rounded-sm text-sm font-medium after:absolute after:inset-0 after:rounded-xl ${focusHaloFlush}`}
              >
                <TruncatedText text={titleOf(asset)} lines={2} className="text-pretty" />
              </AssetLink>
              <p className="mt-0.5 flex min-w-0 items-start gap-1.5 text-xs text-muted-foreground">
                {asset.isSensitive && <SensitiveLabel />}
                <TruncatedText text={captionDetail(asset)} lines={2} className="min-w-0 flex-1" />
              </p>
            </div>
            <div className="relative z-10 hidden h-9 flex-1 sm:block">
              <Waveform media={asset.media} bars={96} />
            </div>
            {/* Wide enough for "1:42 / 3:27", so starting a recording doesn't
                move the waveform beside it. */}
            <PlaybackTime
              media={asset.media}
              className="w-20 shrink-0 text-right text-xs text-muted-foreground"
            />
          </li>
        ) : null,
      )}
    </ul>
  );
}

/** A waiting card: a picture at a shape pictures usually come in, or, without one, a recording. */
interface WaitingCard {
  id: string;
  shape: number | null;
}

/** Flags, paintings, film stills, and portraits, in an order that doesn't line up across a row. */
const WAITING_SHAPES = [1.5, 0.75, 1.33, 1.78, 1, 0.8, 1.5, 2, 1.2, 0.75, 1.33, 1.5, 1.78, 1];
const WAITING_PICTURES: WaitingCard[] = WAITING_SHAPES.map((shape, at) => ({ id: `${at}`, shape }));
/** The same, with a recording every so often, as the whole library mixes them in. */
const WAITING_MIXED: WaitingCard[] = WAITING_PICTURES.map((card, at) =>
  at % 5 === 2 ? { ...card, shape: null } : card,
);

function waitingHeight(card: WaitingCard, columnWidth: number): number {
  return card.shape === null ? AUDIO_CARD : columnWidth / card.shape + CAPTION;
}

/** A waveform's outline while its recording loads: bars that rise and fall like one. */
const WAITING_BARS = [
  0.35, 0.6, 0.45, 0.8, 0.5, 0.7, 0.3, 0.55, 0.9, 0.4, 0.65, 0.5, 0.75, 0.35, 0.6, 0.85, 0.45, 0.55,
  0.7, 0.4, 0.6, 0.3, 0.5, 0.8, 0.45, 0.65, 0.35, 0.55,
];

function WaitingWaveform({ bars, className }: { bars: number; className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("flex animate-pulse items-center justify-between overflow-hidden", className)}
    >
      {Array.from({ length: bars }, (_, at) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: a bar is only its place in the waveform
          key={at}
          className="w-[3px] shrink-0 rounded-full bg-foreground/10"
          style={{ height: `${(WAITING_BARS[at % WAITING_BARS.length] ?? 0.5) * 100}%` }}
        />
      ))}
    </div>
  );
}

/** A name and the line under it, as every card's caption has. */
function WaitingCaption() {
  return (
    <div className="px-0.5 pt-2 pb-1">
      <Skeleton className="h-3.5 w-3/4 rounded" />
      <Skeleton className="mt-2 h-3 w-1/2 rounded" />
    </div>
  );
}

function WaitingPicture({ shape }: { shape: number }) {
  return (
    <div>
      <Skeleton className="rounded-xl" style={{ aspectRatio: shape }} />
      <WaitingCaption />
    </div>
  );
}

function WaitingRecording() {
  return (
    <div className="rounded-xl bg-foreground/5 p-3 ring-1 ring-foreground/10">
      <div className="flex items-center gap-3">
        <Skeleton className="size-10 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1">
          <Skeleton className="h-3.5 w-3/4 rounded" />
          <Skeleton className="mt-2 h-3 w-1/2 rounded" />
        </div>
      </div>
      <WaitingWaveform bars={48} className="mt-3 h-10" />
      <div className="mt-2 flex h-5 items-center">
        <Skeleton className="h-3 w-1/3 rounded" />
      </div>
    </div>
  );
}

/**
 * The library while it loads, laid out by the same columns as the assets that
 * will replace it, so nothing moves when they arrive. Recordings on their own
 * load as a track list, as they show.
 */
export function AssetGridSkeleton({ kind }: { kind?: AssetKind }) {
  if (kind === "audio") return <AudioListSkeleton />;
  return (
    <div aria-busy>
      <Masonry
        items={kind ? WAITING_PICTURES : WAITING_MIXED}
        keyOf={card => card.id}
        heightOf={waitingHeight}
        render={card =>
          card.shape === null ? <WaitingRecording /> : <WaitingPicture shape={card.shape} />
        }
      />
    </div>
  );
}

function AudioListSkeleton() {
  return (
    <ul aria-busy className="flex flex-col gap-1">
      {Array.from({ length: 6 }, (_, at) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholders are only their position
        <li key={at} className="flex items-center gap-4 px-3 py-2.5">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="w-56 min-w-0 shrink-0 max-sm:flex-1">
            <Skeleton className="h-3.5 w-3/4 rounded" />
            <Skeleton className="mt-2 h-3 w-1/2 rounded" />
          </div>
          <WaitingWaveform bars={96} className="hidden h-9 flex-1 sm:flex" />
          <Skeleton className="h-3 w-12 shrink-0 rounded" />
        </li>
      ))}
    </ul>
  );
}

function useWidth() {
  const [width, setWidth] = useState(0);
  const measure = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;
    const read = () => setWidth(element.clientWidth);
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { measure, width };
}

/** How far ahead of the foot the next page is asked for. */
const REACH = "900px";

/**
 * The grid keeps going: the next page is asked for when the foot of this one
 * comes within a screen of you. A button is kept for when that fails, and
 * for keyboards that would rather press than scroll.
 */
export function GridFoot({
  hasNextPage,
  isFetchingNextPage,
  failed,
  onLoadMore,
  noun = "assets",
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  failed: boolean;
  onLoadMore: () => void;
  noun?: string;
}) {
  const foot = useRef<HTMLDivElement>(null);
  const enabled = hasNextPage && !isFetchingNextPage && !failed;

  useEffect(() => {
    const element = foot.current;
    if (!element || !enabled) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onLoadMore();
      },
      { root: document.querySelector(SCREEN_SCROLLER), rootMargin: REACH },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, onLoadMore]);

  return (
    <div ref={foot} className="flex h-14 items-center justify-center">
      {isFetchingNextPage && <p className="text-sm text-muted-foreground">Loading more {noun}…</p>}
      {failed && (
        <Button type="button" variant="outline" size="sm" onClick={onLoadMore}>
          Try loading more
        </Button>
      )}
      {hasNextPage && !isFetchingNextPage && !failed && (
        <button type="button" onClick={onLoadMore} className="sr-only focus:not-sr-only">
          Load more {noun}
        </button>
      )}
    </div>
  );
}
